import { useSettings } from 'app/main/components/hooks';
import {
	DetailsChildrenTab,
	DetailsSectionFiles,
	type DetailsPageContentProps,
	type DetailsPageProps
} from 'app/main/components/page-layouts';

import PeopleListItem from 'app/main/modules/people/PeopleListItem';
import ChronologyListItem from 'app/main/modules/chronology/ChronologyListItem';
import MarketingActionsListItem from 'app/main/modules/marketingactions/MarketingActionsListItem';
import CommercialCampaignsListItem from 'app/main/modules/commercialcampaigns/CommercialCampaignsListItem';
import OpportunitiesListItem from 'app/main/modules/opportunities/OpportunitiesListItem';
import ContractsListItem from 'app/main/modules/contracts/ContractsListItem';
import ErpQuotationsListItem from 'app/main/modules/erp-quotations/ErpQuotationsListItem';
import ErpOrdersListItem from 'app/main/modules/erp-orders/ErpOrdersListItem';
import OrdersListItem from 'app/main/modules/orders/OrdersListItem';
import ProjectsListItem from 'app/main/modules/projects/ProjectsListItem';
import ActivitiesListItem from 'app/main/modules/activities/ActivitiesListItem';
import WorkReportsListItem from 'app/main/modules/workreports/WorkReportsListItem';
import TicketsListItem from 'app/main/modules/tickets/TicketsListItem';

import DemsRecipientsExternalTab from 'app/main/modules/dems/dems-recipients/DemsRecipientsExternalTab';
import KpisGoalsValuesDetailsTab from 'app/main/submodules/kpis/tabs/KpisGoalsValuesDetailsTab';
import getInvoicesTableColumns from 'app/main/submodules/invoices/InvoicesTableColumns';
import PhoneCallsChartTab from 'app/main/submodules/phonecalls/PhoneCallsChartTab';

import CompaniesDetailsTab from './tabs/CompaniesDetailsTab';
import CompaniesContactTab from './tabs/CompaniesContactTab';
import CompaniesCreditsafeTab from './tabs/CompaniesCreditsafeTab';
import CompaniesAtokaTab from './tabs/CompaniesAtokaTab';

import CompaniesListItem from '../CompaniesListItem';

function CompaniesDocsTab(props: DetailsPageContentProps) {
	const { sysSettings } = useSettings();

	return (
		<DetailsSectionFiles
			folderPath={`${sysSettings[`docs_${props.model.core_type}_path`]}\\${props.model.comp_foldername}`}
		/>
	);
}

function CompaniesReservedDocsTab(props: DetailsPageContentProps) {
	const { sysSettings } = useSettings();

	return (
		<DetailsSectionFiles
			folderPath={`${sysSettings[`docs_${props.model.core_type}_path_reserved`]}\\${props.model.comp_foldername}`}
		/>
	);
}

const tabs: DetailsPageProps['tabs'] = [
	{
		id: 'summary',
		title: 'TAB_SUMMARY',
		content: props => <CompaniesDetailsTab {...props} />
	},
	{
		id: 'contacts',
		title: 'TAB_CONTACTS',
		disabled: ({ isEdit }) => isEdit,
		content: ({ model }) => <CompaniesContactTab itemUid={model.comp_uid} />
	},
	{
		id: 'people',
		title: 'MODULE_PEOPLE_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer }) => authorizer.cannot('view', 'people'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="people"
				content={{ type: 'list', itemLayout: PeopleListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'peop_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'kpis-goals',
		title: 'MODULE_SYS_KPIS_GOALS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('sys_kpis_goals') || authorizer.cannot('view', 'sys_kpis_goals'),
		content: ({ model, updateRefresh, refresh }) => (
			<KpisGoalsValuesDetailsTab
				aggregation={{ module: 'companies', value: model.comp_uid }}
				refresh={refresh}
				updateRefresh={updateRefresh}
			/>
		)
	},
	{
		id: 'chronology',
		title: 'MODULE_CHRONOLOGY_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('chronology') || authorizer.cannot('view', 'chronology'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="chronology"
				content={{ type: 'list', itemLayout: ChronologyListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'chro_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'activities',
		title: 'MODULE_ACTIVITIES_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('activities') || authorizer.cannot('view', 'activities'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="activities"
				content={{ type: 'list', itemLayout: ActivitiesListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'acti_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'opportunities',
		title: 'MODULE_OPPORTUNITIES_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('opportunities') || authorizer.cannot('view', 'opportunities'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="opportunities"
				content={{ type: 'list', itemLayout: OpportunitiesListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'oppo_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'docs',
		title: 'MODULE_DOCS_NAME',
		disabled: ({ model, sysSettings, isEdit }) => isEdit || !sysSettings[`docs_${model.core_type}_path`],
		hidden: ({ authorizer, sysSettings }) =>
			sysSettings.docs_companies_enabled !== 'true' || authorizer.cannot('view', 'docs'),
		content: CompaniesDocsTab
	},
	{
		id: 'docs-reserved',
		title: 'MODULE_DOCS_NAME_RESERVED',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer, sysSettings }) =>
			sysSettings.docs_companies_enabled !== 'true' ||
			sysSettings.docs_reserved_enabled !== 'true' ||
			authorizer.cannot('view', 'docs_reserved'),
		content: CompaniesReservedDocsTab
	},
	{
		id: 'orders',
		title: 'MODULE_ORDERS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer, model }) =>
			isModuleDisabled('orders') ||
			authorizer.cannot('view', 'orders') ||
			![1, 2, 5, 30].includes(model.comp_core_id),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="orders"
				content={{ type: 'list', itemLayout: OrdersListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'ords_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'commercialcampaigns',
		title: 'MODULE_COMMERCIALCAMPAIGNS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('commercialcampaigns') || authorizer.cannot('view', 'commercialcampaigns'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="commercialcampaigns_rows"
				content={{ type: 'list', itemLayout: CommercialCampaignsListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'cmrw_table_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'marketingactions',
		title: 'MODULE_MARKETINGACTIONS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('marketingactions') || authorizer.cannot('view', 'marketingactions'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="marketingactions_rows"
				content={{ type: 'list', itemLayout: MarketingActionsListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'marw_table_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'dems',
		title: 'MODULE_DEMS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('dems') ||
			isModuleDisabled('dems_recipients') ||
			authorizer.cannot('view', 'dems_recipients'),
		content: ({ model }) => <DemsRecipientsExternalTab itemUid={model.comp_uid} />
	},
	{
		id: 'erp-quotations',
		title: 'MODULE_ERP_QUOTATIONS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('erp-quotations') || authorizer.cannot('view', 'erp-quotations'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="erp_quotations"
				content={{ type: 'list', itemLayout: ErpQuotationsListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'quot_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'erp-orders',
		title: 'MODULE_ERP_ORDERS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('erp-orders') || authorizer.cannot('view', 'erp-orders'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="erp_orders"
				content={{ type: 'list', itemLayout: ErpOrdersListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'ords_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'projects',
		title: 'MODULE_PROJECTS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('projects') || authorizer.cannot('view', 'projects'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="projects"
				content={{ type: 'list', itemLayout: ProjectsListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'proj_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'workreports',
		title: 'MODULE_WORKREPORTS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('workreports') || authorizer.cannot('view', 'workreports'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="workreports"
				content={{ type: 'list', itemLayout: WorkReportsListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'wore_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'tickets',
		title: 'MODULE_TICKETS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('tickets') || authorizer.cannot('view', 'tickets'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="tickets"
				content={{ type: 'list', itemLayout: TicketsListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'tick_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'invoices',
		title: 'MODULE_INVOICES_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('invoices') || authorizer.cannot('view', 'invoices'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="invoices"
				content={{ type: 'table', getColumns: getInvoicesTableColumns }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[{ filt_field: 'invc_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'phonecalls',
		title: 'MODULE_PHONECALLS_NAME',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ isModuleDisabled, authorizer }) =>
			isModuleDisabled('phonecalls') || authorizer.cannot('view', 'phonecalls'),
		content: ({ model }) => (
			<PhoneCallsChartTab
				defaultFilters={[{ filt_field: 'comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }]}
			/>
		)
	},
	{
		id: 'child-companies',
		title: 'DETAILS_TABS_CHILDREN',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer }) =>
			authorizer.cannot('view', 'companies') || authorizer.cannot('view', 'child-companies'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="companies"
				content={{ type: 'list', itemLayout: CompaniesListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[
					{ filt_field: 'comp_parent_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }
				]}
			/>
		)
	},
	{
		id: 'lead-companies',
		title: 'DETAILS_TABS_LEAD_COMPANIES',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer }) =>
			authorizer.cannot('view', 'companies') || authorizer.cannot('view', 'lead-companies'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="companies"
				content={{ type: 'list', itemLayout: CompaniesListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[
					{ filt_field: 'comp_broker_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }
				]}
			/>
		)
	},
	{
		id: 'lead-people',
		title: 'DETAILS_TABS_LEAD_PERSON',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer }) => authorizer.cannot('view', 'people') || authorizer.cannot('view', 'lead-people'),
		content: ({ model, updateRefresh, refresh }) => (
			<DetailsChildrenTab
				module="people"
				content={{ type: 'list', itemLayout: PeopleListItem }}
				refresh={refresh}
				updateRefresh={updateRefresh}
				defaultFilters={[
					{ filt_field: 'peop_broker_comp_uid', filt_clause: 'equals', filt_value: model.comp_uid }
				]}
			/>
		)
	},
	{
		id: 'creditsafe',
		title: 'TAB_COMPANIES_CREDITSAFE',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer, sysSettings }) =>
			sysSettings.creditsafe_integration !== 'true' ||
			authorizer.cannot('view', 'companies_financialstatements_creditsafe'),
		content: ({ model, updateRefresh }) => (
			<CompaniesCreditsafeTab
				compUid={model.comp_uid}
				creditsafeId={model.comp_creditsafe_id}
				creditsafeUpdateDate={model.comp_creditsafe_d_update}
				country={model.ctry_iso}
				vatNumber={model.comp_vatnumber}
				updateRefresh={updateRefresh}
			/>
		)
	},
	{
		id: 'atoka',
		title: 'TAB_COMPANIES_ATOKA',
		disabled: ({ isEdit }) => isEdit,
		hidden: ({ authorizer, sysSettings }) =>
			sysSettings.creditsafe_integration !== 'true' ||
			authorizer.cannot('view', 'companies_financialstatements_atoka'),
		content: ({ model, updateRefresh }) => (
			<CompaniesAtokaTab
				compUid={model.comp_uid}
				atokaId={model.comp_atoka_id}
				atokaUpdateDate={model.comp_atoka_d_update}
				vatNumber={model.comp_vatnumber}
				updateRefresh={updateRefresh}
			/>
		)
	}
];

export default tabs;
